import React, { useRef, useState, useCallback } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { Download, Calendar, Users, Building2, CheckCircle2, Clock, Link2 } from 'lucide-react';

/**
 * Europcar × Glowytics — 21 Temmuz 2026 toplantı notları.
 * Kullanıcının ham toplantı notlarından temize çekilmiştir; içerik eklenmemiş,
 * yalnızca yazım düzeltmeleri ve gruplama yapılmıştır.
 * Sadece Europcar brand'inde görünür (App.js + Sidebar restrictedBrands).
 */

const MEETING_DATE = '21.07.2026';

// tone: 'date' (takvimli) · 'dep' (bir başka işe bağlı) · 'owner' (sorumlu — SADECE notlarda açıkça yazanlar)
const SECTIONS = [
  {
    id: 'gmb',
    no: '01',
    label: 'Google My Business',
    title: 'Yorum yönetimi, profil standardizasyonu ve rezervasyon kanalı',
    items: [
      {
        head: 'Google Business API erişimi talep edilecek.',
        body: "Google Business yönetimi kapsamında raporlama ve müşteri yorumlarının yanıtlanmasına ilişkin süreç değerlendirildi. Bu kapsamda Google'dan API erişimi talep edilecek.",
        tags: [{ tone: 'date', text: 'Başvuru: 21.07.2026' }, { tone: 'owner', text: 'Sorumlu: Glowytics' }],
      },
      {
        head: 'Tek ekrandan merkezi yorum yönetimi kurulacak.',
        body: 'API entegrasyonunun tamamlanmasının ardından işletme puanları, gelen yorum sayısı ve müşteri yorumlarının görüntülenmesi ile yanıtlanması tek bir ekran üzerinden merkezi olarak yönetilebilecek.',
        tags: [{ tone: 'dep', text: 'Bağımlı: API erişimi' }],
      },
      {
        head: 'Tüm ofislerin Google profilleri gözden geçirilecek.',
        body: 'Google My Business platformunun rezervasyon kanalı olarak daha etkin kullanılması değerlendirildi. Tüm ofislerin Google profilleri incelenecek; profil isimleri, fotoğraflar ve genel hesap düzeni standartlar doğrultusunda gözden geçirilecek.',
        tags: [],
      },
      {
        head: 'Ofisler arası Google My Business yarışması başlatılacak.',
        body: 'Gerekli iyileştirmelerin tamamlanmasının ardından, ofisler arasında Google My Business performansını teşvik etmek amacıyla bir yarışma başlatılacak.',
        tags: [{ tone: 'date', text: 'En geç Ağustos 2026' }, { tone: 'dep', text: 'Bağımlı: Profil iyileştirmeleri' }],
      },
    ],
  },
  {
    id: 'haritalar',
    no: '02',
    label: 'Yandex & Apple Haritalar',
    title: 'Lokasyon tanımlarının tamamlanması',
    items: [
      { head: 'Yandex ve Apple Maps lokasyon tanımlamaları kontrol edilecek, eksikler tamamlanacak.', body: null, tags: [] },
      { head: 'Sonrasında Yandex reklamları değerlendirilecek.', body: null, tags: [{ tone: 'dep', text: 'Bağımlı: Lokasyon tanımları' }] },
    ],
  },
  {
    id: 'raporlama',
    no: '03',
    label: 'Glowytics Raporlama',
    title: 'Panel erişimi',
    items: [
      {
        head: 'Glowytics ekranına erişim verilecek.',
        body: 'Kapsam: Analytics, reklam hesapları ve Google My Business verileri.',
        tags: [],
      },
    ],
  },
  {
    id: 'programatik',
    no: '04',
    label: 'Programatik',
    title: 'Lokasyon bazlı kampanya ve günlük verimlilik raporlaması',
    items: [
      {
        head: 'Programatik reklam konusunda çalışma yapılıp bilgi verilecek.',
        body: 'Lokasyon özelinde kampanya kurgusu için değerlendirilecek.',
        tags: [{ tone: 'owner', text: 'Sorumlu: Glowytics' }],
      },
      {
        head: 'Verimlilik bilgisi günlük olarak ofis bazlı paylaşılacak.',
        body: 'Aynı veri Glowytics rapor ekranına da eklenecek.',
        tags: [{ tone: 'date', text: 'Günlük' }],
      },
    ],
  },
  {
    id: 'isbirlikleri',
    no: '05',
    label: 'İş Birlikleri',
    title: 'Pegasus, Beşiktaş ve yaz kampanyası',
    items: [
      { head: 'Pegasus ve Beşiktaş uygulama iş birlikleri özelinde çalışma yapılacak.', body: null, tags: [] },
      {
        head: '“Europcar’dan araç kirala, uçak biletin bedava” söylemiyle reklam çıkılacak.',
        body: null,
        tags: [{ tone: 'date', text: 'Ağustos – Eylül 2026' }],
      },
    ],
  },
  {
    id: 'seo',
    no: '06',
    label: 'SEO',
    title: 'Ajans devri, backlink raporlaması ve blog içerik akışı',
    items: [
      {
        head: "Mevcut SEO ajansından açık task'lar alınacak.",
        body: "Sonrasında Glowytics yeni task'ları oluşturacak.",
        tags: [{ tone: 'owner', text: 'Sorumlu: Glowytics' }],
      },
      { head: 'Her ay backlink raporu hazırlanacak.', body: null, tags: [{ tone: 'date', text: 'Aylık' }, { tone: 'owner', text: 'Sorumlu: Yeni ajans' }] },
      {
        head: 'Hazırlanan blog içerikleri yeni ajans tarafından girilecek.',
        body: 'İçerikler yayına alınmadan önce kurumsal iletişim ile paylaşılacak.',
        tags: [{ tone: 'owner', text: 'Onay: Kurumsal İletişim' }],
      },
    ],
  },
  {
    id: 'cpc',
    no: '07',
    label: 'CPC',
    title: 'Hesap erişimi ve inceleme',
    items: [{ head: 'Reklam hesaplarına erişim verilecek, inceleme yapılacak.', body: null, tags: [] }],
  },
  {
    id: 'analytics',
    no: '08',
    label: 'Analytics',
    title: "Yetkilendirme, eksik event'ler ve customer journey",
    items: [
      { head: 'Yetki verilip inceleme yapılacak.', body: null, tags: [] },
      { head: "Eksik event'ler kurulacak.", body: null, tags: [{ tone: 'dep', text: 'Bağımlı: Analytics yetkisi' }] },
      { head: 'Customer journey takip edilmeye başlanacak.', body: null, tags: [{ tone: 'dep', text: 'Bağımlı: Event kurulumu' }] },
    ],
  },
  {
    id: 'crm',
    no: '09',
    label: 'CRM',
    title: 'Veri seti hazırlığı',
    items: [{ head: 'Veri seti hazırlandıktan sonra iletişime başlanacak.', body: null, tags: [{ tone: 'dep', text: 'Bağımlı: Veri seti' }] }],
  },
];

const SUMMARY = [
  ['Google My Business', 'Google Business API erişim başvurusu', 'Glowytics', '21.07.2026'],
  ['Google My Business', 'Tek ekrandan yorum görüntüleme & yanıtlama', null, 'API sonrası'],
  ['Google My Business', 'Tüm ofis profillerinin standardizasyonu (isim, foto, hesap düzeni)', null, '—'],
  ['Google My Business', 'Ofisler arası GMB performans yarışması', null, 'En geç Ağustos'],
  ['Haritalar', 'Yandex & Apple Maps lokasyon tanımlarının kontrolü/tamamlanması', null, '—'],
  ['Haritalar', 'Yandex reklamlarının değerlendirilmesi', null, 'Lokasyon sonrası'],
  ['Raporlama', 'Glowytics ekranı erişimi (Analytics, reklam, GMB)', null, '—'],
  ['Programatik', 'Programatik reklam çalışması ve bilgilendirme', 'Glowytics', '—'],
  ['Programatik', 'Ofis bazlı verimlilik paylaşımı + rapor ekranına eklenmesi', null, 'Günlük'],
  ['İş Birlikleri', 'Pegasus ve Beşiktaş app iş birliği çalışması', null, '—'],
  ['İş Birlikleri', '“Araç kirala, uçak biletin bedava” kampanyası', null, 'Ağustos – Eylül'],
  ['SEO', "Mevcut ajanstan açık task'ların alınması", null, '—'],
  ['SEO', "Yeni task'ların oluşturulması", 'Glowytics', 'Devir sonrası'],
  ['SEO', 'Backlink raporu', 'Yeni ajans', 'Aylık'],
  ['SEO', 'Blog içeriklerinin girilmesi (önce kurumsal iletişim onayı)', null, '—'],
  ['CPC', 'Hesap erişimlerinin verilmesi ve inceleme', null, '—'],
  ['Analytics', 'Yetkilendirme ve inceleme', null, '—'],
  ['Analytics', "Eksik event'lerin kurulması", null, 'Yetki sonrası'],
  ['Analytics', 'Customer journey takibinin başlatılması', null, 'Event sonrası'],
  ['CRM', 'Veri seti hazırlığı sonrası iletişimin başlatılması', null, '—'],
];

const TAG_TONES = {
  date: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  dep: 'bg-amber-50 text-amber-700 ring-amber-200',
  owner: 'bg-slate-100 text-slate-600 ring-slate-200',
};

const Tag = ({ tone, text }) => (
  <span className={`inline-flex items-center rounded-md px-2 py-1 text-[11px] font-semibold ring-1 ring-inset ${TAG_TONES[tone] || TAG_TONES.owner}`}>
    {text}
  </span>
);

const Stat = ({ value, label }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4">
    <p className="text-[28px] font-bold leading-none tracking-tight text-slate-900">{value}</p>
    <p className="mt-1.5 text-xs text-slate-500">{label}</p>
  </div>
);

export default function EuropcarMeetingNotes() {
  const sheetRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const exportPdf = useCallback(async () => {
    if (exporting || !sheetRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(sheetRef.current, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
      const img = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const m = 8;
      const w = pw - m * 2;
      const h = (canvas.height * w) / canvas.width;
      let y = m;
      let rem = h;
      pdf.addImage(img, 'JPEG', m, y, w, h);
      rem -= ph - m * 2;
      while (rem > 0) {
        pdf.addPage();
        y = m - (h - rem);
        pdf.addImage(img, 'JPEG', m, y, w, h);
        rem -= ph - m * 2;
      }
      pdf.save('Europcar_Toplanti_Notlari_21_07_2026.pdf');
    } catch (e) {
      console.error('PDF export failed:', e);
    } finally {
      setExporting(false);
    }
  }, [exporting]);

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-8">
      <div ref={sheetRef} className="mx-auto max-w-5xl bg-white">
        {/* ---------- Başlık ---------- */}
        <header className="border-b border-slate-200 px-6 pb-8 pt-8 sm:px-10">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-emerald-700">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                Toplantı Notları
              </span>
              <h1 className="mt-4 text-3xl font-bold leading-tight tracking-tight text-slate-900 sm:text-4xl">
                Europcar × Glowytics
                <br />
                21 Temmuz 2026
              </h1>
              <p className="mt-3 max-w-xl text-[15px] text-slate-500">
                Toplantıda görüşülen başlıklar, alınan kararlar ve takip edilecek aksiyon maddeleri.
              </p>
            </div>
            <button
              onClick={exportPdf}
              disabled={exporting}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-700 disabled:opacity-60"
            >
              <Download className="h-4 w-4" />
              {exporting ? 'Hazırlanıyor…' : 'PDF indir'}
            </button>
          </div>

          <div className="mt-7 flex flex-wrap gap-x-10 gap-y-4">
            <div className="flex items-start gap-2.5">
              <Calendar className="mt-0.5 h-4 w-4 text-slate-400" />
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Tarih</p>
                <p className="text-sm font-semibold text-slate-900">{MEETING_DATE}</p>
              </div>
            </div>
            <div className="flex items-start gap-2.5">
              <Building2 className="mt-0.5 h-4 w-4 text-slate-400" />
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Konu</p>
                <p className="text-sm font-semibold text-slate-900">Dijital pazarlama devir &amp; yol haritası</p>
              </div>
            </div>
            <div className="flex items-start gap-2.5">
              <Users className="mt-0.5 h-4 w-4 text-slate-400" />
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Taraflar</p>
                <p className="text-sm font-semibold text-slate-900">Europcar · Glowytics</p>
              </div>
            </div>
          </div>
        </header>

        <div className="px-6 pb-12 sm:px-10">
          {/* ---------- Özet kutuları ---------- */}
          <div className="mt-8 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat value="9" label="Ana başlık" />
            <Stat value="20" label="Aksiyon maddesi" />
            <Stat value="5" label="Takvime bağlı madde" />
            <Stat value="4" label="Erişim/yetki bekleyen" />
          </div>

          {/* ---------- İçindekiler ---------- */}
          <nav className="mt-6 rounded-xl border border-slate-200 bg-white p-5">
            <p className="mb-3 text-[11px] font-bold uppercase tracking-wider text-slate-400">İçindekiler</p>
            <div className="grid gap-x-6 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
              {SECTIONS.map((s) => (
                <a
                  key={s.id}
                  href={`#ec-${s.id}`}
                  className="flex items-baseline gap-2.5 rounded-md px-2 py-1.5 text-sm text-slate-700 transition hover:bg-emerald-50"
                >
                  <span className="text-[11px] font-bold tabular-nums text-emerald-600">{s.no}</span>
                  {s.label}
                </a>
              ))}
            </div>
          </nav>

          {/* ---------- Bölümler ---------- */}
          {SECTIONS.map((s) => (
            <section key={s.id} id={`ec-${s.id}`} className="mt-10 scroll-mt-6">
              <div className="flex items-center gap-3">
                <span className="grid h-7 w-7 flex-none place-items-center rounded-md bg-emerald-50 text-[12px] font-bold tabular-nums text-emerald-700">
                  {s.no}
                </span>
                <h2 className="text-[13px] font-bold uppercase tracking-wider text-emerald-700">{s.label}</h2>
              </div>
              <p className="mt-2 text-[22px] font-bold leading-snug tracking-tight text-slate-900">{s.title}</p>

              <div className="mt-5 space-y-3">
                {s.items.map((it, i) => (
                  <div key={i} className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
                    <p className="flex items-start gap-2.5 text-[15px] font-semibold text-slate-900">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-emerald-500" />
                      <span>{it.head}</span>
                    </p>
                    {it.body && <p className="mt-2 pl-[26px] text-[14px] leading-relaxed text-slate-500">{it.body}</p>}
                    {it.tags.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5 pl-[26px]">
                        {it.tags.map((t, j) => (
                          <Tag key={j} tone={t.tone} text={t.text} />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          ))}

          {/* ---------- Aksiyon özeti ---------- */}
          <section className="mt-12">
            <div className="flex items-center gap-3">
              <span className="grid h-7 w-7 flex-none place-items-center rounded-md bg-emerald-50 text-emerald-700">
                <Clock className="h-3.5 w-3.5" />
              </span>
              <h2 className="text-[13px] font-bold uppercase tracking-wider text-emerald-700">Aksiyon Özeti</h2>
            </div>
            <p className="mt-2 text-[22px] font-bold leading-snug tracking-tight text-slate-900">Tek bakışta takip listesi</p>

            <div className="mt-5 overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full min-w-[640px] border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="w-[20%] px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-400">Başlık</th>
                    <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-400">Aksiyon</th>
                    <th className="w-[18%] px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-400">Zaman</th>
                  </tr>
                </thead>
                <tbody>
                  {SUMMARY.map(([topic, action, owner, when], i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-4 py-3 align-top text-slate-700">{topic}</td>
                      <td className="px-4 py-3 align-top text-slate-900">
                        {action}
                        {owner && <span className="ml-1.5 text-[12px] font-semibold text-slate-500">· {owner}</span>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 align-top text-[13px] text-slate-500">{when}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <footer className="mt-10 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-5 text-[13px] text-slate-400">
            <span>Europcar × Glowytics · 21 Temmuz 2026 toplantı notları</span>
            <a
              href="https://api.glowytics.com/pixel/europcar/toplanti-21-07-2026.html"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-slate-400 transition hover:text-slate-700"
            >
              <Link2 className="h-3.5 w-3.5" />
              Panel dışı paylaşılabilir sürüm
            </a>
          </footer>
        </div>
      </div>
    </div>
  );
}
